import { useEffect, useRef } from 'react';

import { useUpsertMyProfileMutation } from '@/services/socialApi';
import { useTelegram } from '@/hooks/useTelegram';
import { useAppSelector } from '@/store/hooks';
import type { UpsertMyProfileInput } from '@/types/social';

/**
 * Syncs the authenticated user's QSocials profile (displayName + avatar) once
 * per mount/session.
 *
 * The public display name is the user's **QLASH account handle** — their
 * `username` from `/users/me`, falling back to the Telegram `@username`. We
 * deliberately NEVER publish the Telegram first/last name: a player is referred
 * to by their QLASH account, never by their legal name (personal data). QSocials
 * search + friend lists key off this displayName, so publishing the handle is
 * exactly what makes people findable by their QLASH account and not their name.
 *
 * QSocials stores friend/follow rows keyed by Logto `sub`, plus a `userProfiles`
 * collection (displayName + avatar) so lists show a handle instead of a raw sub.
 * The Mini App pushes it via `PUT /social/me/profile` on launch. Idempotent
 * server-side — re-running with the same payload is a no-op write.
 *
 * Mount once at the AppShell layer (after auth), alongside usePresenceHeartbeat.
 * No-op until `bridgeAuthenticated` is true and a usable handle is present
 * (plain-web/dev runs without a Telegram user still sync off the QLASH username).
 */
export function useProfileSync(): void {
  const isAuthenticated = useAppSelector((s) => s.auth.bridgeAuthenticated);
  // QLASH user from `/users/me`: `username` is the public account handle we
  // publish; `id` is the Laravel numeric users.id we self-publish as
  // `externalUserId` so the participants list can resolve us deterministically
  // (immune to Apple Hide-My-Email / social-login email mismatches).
  const qlashUsername = useAppSelector((s) => s.auth.user?.username);
  const qlashUserId = useAppSelector((s) => s.auth.user?.id);
  const { user } = useTelegram();
  const [upsertProfile] = useUpsertMyProfileMutation();

  // Guard so we sync at most once per mount even if deps re-run (the QLASH user
  // is stable once loaded, but RTK's mutation trigger identity is not).
  const syncedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || syncedRef.current) return;

    // QLASH account handle ONLY — never the Telegram legal name. Prefer the
    // QLASH username; fall back to the Telegram @username (also a public handle).
    const displayName = qlashUsername?.trim() || user?.username?.trim() || null;
    if (!displayName) return; // no handle to publish yet

    const payload: UpsertMyProfileInput = { displayName };
    if (user?.photo_url) payload.avatarUrl = user.photo_url;
    // Only publish externalUserId once we actually know our Laravel id.
    // Omitting it leaves the server-stored value untouched (per contract).
    if (qlashUserId != null) payload.externalUserId = String(qlashUserId);

    syncedRef.current = true;
    // Fire-and-forget — a failed profile sync must never block the UI.
    upsertProfile(payload)
      .unwrap()
      .catch((err) => {
        // Allow a retry on the next auth/user change if the write failed.
        syncedRef.current = false;
        if (import.meta.env.DEV) {
          console.debug('[profile-sync] upsert failed:', err);
        }
      });
  }, [isAuthenticated, qlashUsername, qlashUserId, user, upsertProfile]);
}
